import React, {
    createContext,
    useState,
    useContext,
    useReducer,
    useLayoutEffect,
    useEffect,
    useRef,
    PropsWithChildren,
    ReactElement,
} from 'react';
import {useUserProfile, useCountries} from 'hooks/User';
import ReactGA from 'react-ga4';

import {
    User,
    invalidateUser,
    useGetUserQuery,
} from 'components/customer/Auth/store/userSlice';
import {Loader} from 'shared/helpers';
import {useAppDispatch} from 'store/customer';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {BaseConfigInterface} from 'config/entity/BaseConfigInterface';

type SuccessDialog = {
    type?: string;
    status: boolean;
    message: string;
    link: string;
};

type DisabledProperty = {
    propertyName: string;
    message: string;
};

type ApplicationContextType = {
    smallLayout?: boolean;
    userProfile?: Partial<User>;
    isMobileMenuVisible?: boolean;
    setIsMobileMenuVisible?: (isVisible: boolean) => void;
    setDisabledProperties?: (action: {type: string; payload: any[]}) => void;
    stateLabel?: string;
    places?: {
        Countries?: Record<
            string,
            {
                id: number;
                name: string;
            }[]
        >;
    };
    userLevel?: keyof BaseConfigInterface;
    isLeftDashboardButtonVisible?: boolean;
    setIsLeftDashboardButtonVisible?: (v: boolean) => void;
    refreshUserData?: () => void;
    refreshDashboard?: {status: boolean};
    setRefreshDashboard?: (v: {status: boolean}) => void;
    successDialog?: SuccessDialog[];
    setSuccessDialog?: (v: SuccessDialog[]) => void;
    sideBarOverlays?: boolean;
    setSideBarOverlays?: (v: boolean) => void;
    resultReceived?: boolean;
    setResultReceived?: (v: boolean) => void;
    searchOnText?: string;
    setsearchOnText?: (v: string) => void;
    searchOnButton?: string;
    setsearchOnButton?: (v: string) => void;
    isSearching?: boolean;
    setisSearching?: (v: boolean) => void;
    searchInput?: React.RefObject<HTMLInputElement>;
    disableProperties?: DisabledProperty[];
    alternateCartContent?: ReactElement;
    setAlternateCartContent?: (v: ReactElement) => void;
};

export const ApplicationContext: React.Context<ApplicationContextType> =
    createContext({});

export const useAppContext = () => useContext(ApplicationContext);

const STANDARD_SIZE = 1440;

/**
 *
 * This component is used for persisting application data during a session. This component sits on the highest level
 * in component hierarchy so any data persisted in this component is available throughout the application.
 *
 * @param {object} children
 *
 * @return {ApplicationContext.Provider}
 */
export const ApplicationProvider = ({children}: PropsWithChildren<object>) => {
    const dispatch = useAppDispatch();

    const [isLeftDashboardButtonVisible, setIsLeftDashboardButtonVisible] =
        useState<boolean>(false);
    const [smallLayout, setSmallLayout] = useState(false);
    const [isMobileMenuVisible, setIsMobileMenuVisible] =
        useState<boolean>(true);
    const {data: userProfile, isLoading} = useGetUserQuery();
    const [refreshDashboard, setRefreshDashboard] = useState({status: false});
    const [successDialog, setSuccessDialog] = useState([]);
    const [sideBarOverlays, setSideBarOverlays] = useState(false);
    const [resultReceived, setResultReceived] = useState(false);
    const [searchOnText, setsearchOnText] = useState('');
    const [searchOnButton, setsearchOnButton] = useState('');
    const [isSearching, setisSearching] = useState(false);
    const searchInput = useRef();
    const isSmallDevice = isDeviceSmall();
    const [alternateCartContent, setAlternateCartContent] =
        useState<ReactElement>(null);

    // state for disabled proeprties by manufacturer
    const [disableProperties, setDisabledProperties] = useReducer(
        (
            currentState: DisabledProperty[],
            action: {type: 'add'; payload: DisabledProperty[]}
        ) => {
            switch (action.type) {
                // TODO: when we have more unavailable fields in future then use merge (lodash) or some other trick to merge multiple data to current state
                case 'add':
                    return action.payload;
                default:
                    return currentState;
            }
        },
        []
    );

    // state for user profile
    const userLevel = useUserProfile(userProfile);
    const {places, stateLabel} = useCountries(userProfile);

    useEffect(
        () =>
            ReactGA.set({
                user_properties:
                    userProfile &&
                    userProfile.hasOwnProperty('analyticsMetrics')
                        ? userProfile.analyticsMetrics
                        : {},
            }),
        [userProfile]
    );

    useLayoutEffect(() => {
        const availableWidth: number = document.body.clientWidth;

        if (!isSmallDevice && availableWidth <= STANDARD_SIZE) {
            setSmallLayout(true);
        }
    }, [isSmallDevice]);

    const refreshUserData = () => {
        dispatch(invalidateUser());
    };

    if (isLoading) {
        return <Loader loader={isLoading} />;
    }

    return (
        <ApplicationContext.Provider
            value={{
                userProfile,
                places,
                stateLabel,
                userLevel,
                refreshUserData,
                refreshDashboard,
                setRefreshDashboard,
                successDialog,
                setSuccessDialog,
                sideBarOverlays,
                setSideBarOverlays,
                resultReceived,
                setResultReceived,
                searchOnText,
                setsearchOnText,
                searchOnButton,
                setsearchOnButton,
                isSearching,
                setisSearching,
                searchInput,
                isLeftDashboardButtonVisible,
                setIsLeftDashboardButtonVisible,
                disableProperties,
                setDisabledProperties,
                smallLayout,
                isMobileMenuVisible,
                setIsMobileMenuVisible,
                alternateCartContent,
                setAlternateCartContent,
            }}>
            <section
                style={{
                    height: '100%',
                    paddingLeft: 0,
                    paddingRight: 0,
                }}>
                {children}
            </section>
        </ApplicationContext.Provider>
    );
};
